import React, { useState, useContext, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Styles
import './group-modal.scss';
import { DEFAULT_NEW_COURSE_ID } from '../../../containers/console/actions/group-actions';

// Components
import Form from '../../form/form';
import Button from '../../button/button';
import Checkbox, { LABEL_STYLES } from '../../checkbox/checkbox';
import SelectCourse from '../select-course/select-course';

// Context
import SchoolCourseContext from '../context/school-course-context';

// Helpers
import { CONFIG_VARS, ERROR_CODES, PRODUCT_IDS } from '../../../helpers/enums';
import { areNewCoursesAvailable, getCorrectErrorMessage } from '../../../helpers/utils';

function GroupModal(props) {
    const { i18n } = useLocalisation();

    // Props
    const {
        currentId,
        productId,
        initialName,
        saveHandler,
        closeHandler,
        deleteHandler,
        inputId,
        showDeleteButton,
        // Course Specific
        setCountryHandler,
        countrySelectValue,
        countrySelectOptions,
        setYearHandler,
        yearSelectValue,
        yearSelectOptions,
        setCourseHandler,
        courseSelectValue,
        courseSelectOptions,
        setUPFCourseHandler,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        setNewCourseHandler,
        newCourseSelectValue,
        newCourseSelectOptions,
        showNewCourses
    } = props;

    // State variables
    const [name, setName] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [bottomErrorMessage, setBottomErrorMessage] = useState(null);
    const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = useState(false);
    const trimmedName = name.trim();
    const [newCoursesAvailable, setNewCoursesAvailable] = useState(false);

    // School Course Related
    const {
        groupCountryCode,
        isSchoolCoursesEnabled,
        setIsSchoolCoursesEnabled
    } = useContext(SchoolCourseContext);
    const schoolCourseClickHandler = () => {
        setIsSchoolCoursesEnabled(prevState => !prevState);
    };

    // Effects
    useEffect(() => {
        setName(initialName);
    }, [initialName]);

    useEffect(() => {
        const country = isSchoolCoursesEnabled ? groupCountryCode : countrySelectValue;
        setNewCoursesAvailable(areNewCoursesAvailable(yearSelectValue, country));
    }, [yearSelectValue, countrySelectValue, isSchoolCoursesEnabled]);

    // Handlers
    const localInputHandler = (e) => {
        // Reset the error messages
        if (errorMessage !== null) {
            setErrorMessage(null);
        }
        if (bottomErrorMessage !== null) {
            setBottomErrorMessage(null);
        }

        // Otherwise continue forward
        const newName = e.target.value;
        setName(newName);
    };
    const localSaveHandler = async (e) => {
        // Prevent redirect from the default behaviour of a form
        e.preventDefault();

        const value = await saveHandler({
            name: trimmedName
        });

        if (value && value.status === 500) {
            const message = <Trans id={'Error Message - Unable to proceed'} />;
            await setBottomErrorMessage(message);
        } else if (value === true) {
            // Afterwards this handler should close the modal
            await closeHandler();
        } else if (value && value.message) {
            const message = getCorrectErrorMessage(value, i18n);
            await setErrorMessage(message);
        }
    };
    const localDeleteHandler = async () => {
        const value = await deleteHandler();

        if (value && value.code === ERROR_CODES.UnexpectedResponseContent) {
            await setBottomErrorMessage(<Trans id={'Group Modal - label - delete error - existing students'} />);
            setIsDeleteButtonDisabled(true);
        } else if (value && value.status === 500) {
            await setBottomErrorMessage(i18n._(t`Error Message - Unable to proceed`));
        }
    };

    const isSelectDropdownDisabled = (values, propertyName) => values[0][propertyName] === '' && values.length === 1;

    // Creating a list of components in the form
    const countrySelectComponent = (
        <SelectCourse
            id={`select-country-${currentId}`}
            handler={setCountryHandler}
            isDisabled={isSchoolCoursesEnabled}
            title={<Trans id={'Group Modal - label - Select country'} />}
            value={countrySelectValue}
            options={countrySelectOptions}
            selectLabelProps={{
                propertyName: 'code'
            }}
        />
    );
    const yearSelectComponent = (
        <SelectCourse
            id={`select-grade-${currentId}`}
            handler={setYearHandler}
            isDisabled={isSelectDropdownDisabled(yearSelectOptions, 'yearGroupId')}
            title={<Trans id={'Group Modal - label - Select course grade'} />}
            value={yearSelectValue}
            options={yearSelectOptions}
            selectLabelProps={{
                propertyName: 'yearGroupEnum',
                visibleProperty: 'longName'
            }}
        />
    );
    const upfCourseSelectComponent = (
        <SelectCourse
            id={`select-upfcourse-${currentId}`}
            handler={setUPFCourseHandler}
            isDisabled={upfCourseSelectOptions.length === 1}
            title={<Trans id={'Group Modal - label - Skill Quests'} />}
            value={upfCourseSelectValue}
            isRequired={false}
            options={upfCourseSelectOptions}
            selectLabelProps={{
                propertyName: 'id'
            }}
        />
    );

    const newCourseSelectComponent = (
        <SelectCourse
            id={`select-newcourse-${currentId}`}
            handler={setNewCourseHandler}
            isDisabled={(newCourseSelectOptions.length === 1 && newCoursesAvailable) || !newCoursesAvailable}
            title={<Trans id={'Group Modal - label - New Course'} />}
            value={newCourseSelectValue}
            isRequired={newCoursesAvailable}
            options={newCourseSelectOptions}
            selectLabelProps={{
                propertyName: 'id'
            }}
        />
    );

    const courseSelectComponent = (
        <SelectCourse
            id={`select-course-${currentId}`}
            handler={setCourseHandler}
            isDisabled={isSelectDropdownDisabled(courseSelectOptions, 'id')}
            title={<Trans id={'Group Modal - label - Select course'} />}
            value={courseSelectValue}
            options={courseSelectOptions}
            selectLabelProps={{
                propertyName: 'id'
            }}
        />
    );
    const inputNameComponent = (
        <Fragment>
            <label htmlFor={inputId} className="form__label">
                <Trans id={'Group Modal - label - group name'} />
            </label>
            <input
                id={inputId}
                className="form__input"
                autoComplete="off"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                required
                placeholder={i18n._(t`Group Modal - new - name placeholder`)}
                maxLength={CONFIG_VARS.groupNameMaxLength}
                value={name}
                onChange={localInputHandler} />
        </Fragment>
    );

    /**
     * This particular component is only shown for Mathletics, this particular user story mentions that Readiwriter
     * is excluded from seeing this display school courses section. As outlined below:
     *      - https://mathletics.atlassian.net/browse/RR-1186
     */
    const displaySchoolCourseComponent = productId === PRODUCT_IDS.Mathletics ? (
        <div className="form__row flex-grid flex-grid--split">
            <span className="form__label">
                <Trans id={'Group Modal - label - group course'} />
            </span>
            <Checkbox
                key={`school-courses-${currentId}`}
                uid={`school-courses-${currentId}`}
                inputType={LABEL_STYLES.visible}
                isChecked={isSchoolCoursesEnabled}
                callback={schoolCourseClickHandler}
                labelText={<Trans id={'Group Modal - label - Display school courses'} />}
            />
        </div>
    ) : '';

    // Disabled variables
    const isSchoolCourseAvailable = isSchoolCoursesEnabled ? (
        isSchoolCoursesEnabled === true && yearSelectValue !== '' && courseSelectValue !== ''
    ) : (
        countrySelectValue !== '' && isSchoolCoursesEnabled === false
    );

    const isEmpty = v => v === '' || v === undefined;

    const isSubmitButtonDisabled =
        isEmpty(trimmedName) ||
        !isSchoolCourseAvailable ||
        isEmpty(yearSelectValue) ||
        isEmpty(courseSelectValue) ||
        (
            newCoursesAvailable && (
                showNewCourses &&
                newCourseSelectOptions.length > 1 &&
                (isEmpty(newCourseSelectValue) || newCourseSelectValue === DEFAULT_NEW_COURSE_ID)
            )
        ) ||
        (
            upfCourseSelectOptions.length > 1 &&
            isEmpty(upfCourseSelectValue)
        );


    return (
        <Form formHandler={localSaveHandler}>
            <div className="form__row">
                {inputNameComponent}
            </div>
            {
                errorMessage ? (
                    <div className="form__row">
                        <p className="form__error-message">{errorMessage}</p>
                    </div>
                ) : ''
            }
            <hr />
            {displaySchoolCourseComponent}
            <div className="flex-grid flex-grid--occupy">
                <div className="form__row form__row--selects">
                    {countrySelectComponent}
                </div>
                <div className="form__row form__row--selects">
                    {yearSelectComponent}
                </div>
            </div>
            <div className="form__row">
                <h3 className="form__title form__title--section">
                    <Trans id={'Group Modal - label - Default Courses'} />
                </h3>
            </div>
            <div className="flex-grid flex-grid--occupy">
                <div className="form__row">
                    {courseSelectComponent}
                </div>
                {showNewCourses ? (
                    <div className="form__row">
                        {newCourseSelectComponent}
                    </div>
                ) : ''}
                {productId === PRODUCT_IDS.Mathletics
                    ? (<div className="form__row">
                        {upfCourseSelectComponent}
                    </div>)
                    : ''
                }
            </div>
            <div className="form__row form__row--buttons">
                {
                    showDeleteButton ? (
                        <Button
                            visibleText={<Trans id={'Group Modal - Delete'} />}
                            isBtnDisabled={isDeleteButtonDisabled}
                            importedClasses="button--error"
                            callback={localDeleteHandler}
                        />
                    ) : ''
                }
                <Button
                    visibleText={<Trans id={'Group Modal - Save'} />}
                    isBtnDisabled={isSubmitButtonDisabled}
                    buttonType="submit"
                />
            </div>
            {
                bottomErrorMessage ? (
                    <div className="form__row">
                        <p className="form__error-message form__error-message--bottom">
                            {bottomErrorMessage}
                        </p>
                    </div>
                ) : ''
            }
        </Form>
    );
}

GroupModal.propTypes = {
    productId: PropTypes.number.isRequired,
    currentId: PropTypes.number,
    initialName: PropTypes.string.isRequired,
    inputId: PropTypes.string.isRequired,
    closeHandler: PropTypes.func.isRequired,
    saveHandler: PropTypes.func.isRequired,
    deleteHandler: PropTypes.func,
    showDeleteButton: PropTypes.bool,
    // Course Specific
    setCountryHandler: PropTypes.func.isRequired,
    countrySelectValue: PropTypes.string.isRequired,
    countrySelectOptions: PropTypes.array.isRequired,
    setYearHandler: PropTypes.func.isRequired,
    yearSelectValue: PropTypes.string.isRequired,
    yearSelectOptions: PropTypes.array.isRequired,
    setCourseHandler: PropTypes.func.isRequired,
    courseSelectValue: PropTypes.string.isRequired,
    courseSelectOptions: PropTypes.array.isRequired,
    setUPFCourseHandler: PropTypes.func.isRequired,
    upfCourseSelectValue: PropTypes.string,
    upfCourseSelectOptions: PropTypes.array,
    setNewCourseHandler: PropTypes.func.isRequired,
    newCourseSelectValue: PropTypes.string,
    newCourseSelectOptions: PropTypes.array,
    showNewCourses: PropTypes.bool
};

export default GroupModal;



// WEBPACK FOOTER //
// ./components/groups/group-modals/group-modal.js